/**
 * AuthPage – sign in / create account.
 *
 * Supports both:
 *   1. Email + password (JWT cookies)
 *   2. Emergent Google Auth (redirects to auth.emergentagent.com and returns
 *      to /auth/callback where we exchange the session_id).
 *
 * Both paths end up logged in with the same httpOnly cookie pair.
 */
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Envelope, Lock, User, GoogleLogo, SpinnerGap, Compass } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

export default function AuthPage({ mode = 'login' }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const isRegister = mode === 'register';
  const redirectTo = location.state?.from || '/home';

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const res = isRegister
      ? await register(name.trim(), email.trim().toLowerCase(), password)
      : await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) navigate(redirectTo, { replace: true });
    else setError(res.error);
  }

  function handleGoogle() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 lf-chip mb-4" data-testid="auth-wordmark">
            <span className="h-2 w-2 rounded-full bg-[var(--lf-primary)]" />
            LingoFlow
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-[var(--lf-text)]">
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="mt-2 text-[var(--lf-text-muted)]">
            {isRegister
              ? 'Start practicing English or Spanish in minutes.'
              : 'Pick up where you left off.'}
          </p>

          {/* Discreet escape hatch for first-time visitors who land directly
              on /login or /register (e.g. via shared link or paid ad) and
              haven't seen the welcome page yet. Positioned under the
              subtitle so it's discoverable but doesn't compete with the
              primary auth actions below. Inline link styling, not a button,
              to keep visual weight low. Stronger pull on /register since
              that's where hesitant users land. */}
          <Link
            to="/guide"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--lf-primary)] hover:underline"
            data-testid="auth-guide-link"
            aria-label="See how LingoFlow works — 60 second tour"
          >
            <Compass size={16} weight="duotone" />
            See how it works
            <span className="text-xs font-normal text-[var(--lf-text-muted)]">· 60 sec tour</span>
          </Link>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          className="lf-btn lf-btn-secondary w-full"
          data-testid="auth-google-btn"
        >
          <GoogleLogo size={20} weight="bold" />
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs text-[var(--lf-text-muted)]">
          <div className="h-px flex-1 bg-[var(--lf-border)]" />
          or with email
          <div className="h-px flex-1 bg-[var(--lf-border)]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
          {isRegister && (
            <div className="relative">
              <label htmlFor="auth-name" className="lf-sr-only">Your name</label>
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--lf-text-muted)]" aria-hidden="true" />
              <input
                id="auth-name"
                className="lf-input pl-10"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="relative">
            <label htmlFor="auth-email" className="lf-sr-only">Email address</label>
            <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--lf-text-muted)]" aria-hidden="true" />
            <input
              id="auth-email"
              type="email"
              className="lf-input pl-10"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="auth-email-input"
            />
          </div>
          <div className="relative">
            <label htmlFor="auth-password" className="lf-sr-only">Password</label>
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--lf-text-muted)]" aria-hidden="true" />
            <input
              id="auth-password"
              type="password"
              className="lf-input pl-10"
              placeholder="Password (min 6 chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={isRegister ? "new-password" : "current-password"}
              data-testid="auth-password-input"
            />
          </div>

          {error ? (
            <div
              className="rounded-xl px-3 py-2 text-sm"
              style={{ background: '#FCE7E0', color: '#8A2A14' }}
              data-testid="auth-error"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="lf-btn lf-btn-primary w-full"
            data-testid="auth-submit-btn"
          >
            {busy ? <SpinnerGap size={18} className="animate-spin" /> : null}
            {isRegister ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-[var(--lf-text-muted)]">
          {isRegister ? (
            <>Already have an account?{' '}
              <Link to="/login" className="font-semibold text-[var(--lf-primary)]" data-testid="auth-switch-login">Sign in</Link>
            </>
          ) : (
            <>New to LingoFlow?{' '}
              <Link to="/register" className="font-semibold text-[var(--lf-primary)]" data-testid="auth-switch-register">Create an account</Link>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
